// ARQUIVO GERADO — não edite diretamente.
// Edite shared/api-master.js e rode: node shared/sync-api.js
// Sintaxe: Vite (import.meta.env). O sync-api.js gera a versão CRA para o admin.
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // envia cookie httpOnly (refresh)
});

// ─── Token helpers ─────────────────────────────────────────────────────────
export const setToken = (t) => {
  try {
    if (t) localStorage.setItem('token', t);
    else   localStorage.removeItem('token');
  } catch {}
};

const getToken = () => {
  try { return localStorage.getItem('token'); } catch { return null; }
};

// Decodifica o campo `exp` do JWT sem verificar assinatura (seguro no cliente)
function getTokenExp(token) {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp || 0;
  } catch { return 0; }
}

// Segundos restantes até o token expirar (negativo = já expirou)
function tokenSecondsLeft(token) {
  const exp = getTokenExp(token);
  if (!exp) return Infinity; // token sem exp (legado) → trata como válido
  return exp - Math.floor(Date.now() / 1000);
}

// ─── Request interceptor ───────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = getToken();
  if (!config.headers) config.headers = {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  return config;
});

// ─── Refresh centralizado ──────────────────────────────────────────────────
// • 401 fora de /auth/refresh → tenta refresh, reenvia request original
// • 403 → permissão/regra de negócio, não tenta refresh
// • visibilitychange → refresh proativo se token expira em < 15 min

let isRefreshing = false;
let queue = [];

const processQueue = (error, newToken = null) => {
  queue.forEach(({ resolve, reject, originalRequest }) => {
    if (newToken) {
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      resolve(api(originalRequest));
    } else {
      reject(error);
    }
  });
  queue = [];
};

const hardLogout = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    
  } catch {}
  // Notifica a UI para redirecionar ao login — interceptado pelo PrivateRoute
  window.dispatchEvent(new CustomEvent('istudo:logout'));
};

// Refresh proativo ao retornar à aba — protege sessões de prova longas (8h+).
// Se o token expira em < 15 min e o usuário acabou de voltar, renova antes
// que qualquer chamada de API falhe com 401.
const PROACTIVE_THRESHOLD_S = 15 * 60; // 15 minutos

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  const token = getToken();
  if (!token || isRefreshing) return;
  if (tokenSecondsLeft(token) < PROACTIVE_THRESHOLD_S) {
    isRefreshing = true;
    api.post('/auth/refresh')
      .then(({ data }) => {
        const newToken = data?.token;
        if (newToken) { setToken(newToken); processQueue(null, newToken); }
      })
      .catch((err) => { processQueue(err, null); hardLogout(); })
      .finally(() => { isRefreshing = false; });
  }
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config || {};

    // Se não tem status (erro de rede, CORS, etc.), não é caso de refresh
    if (!status) {
      return Promise.reject(error);
    }

    const isRefreshCall =
      typeof originalRequest.url === 'string' &&
      originalRequest.url.includes('/auth/refresh');

    // 🔐 Só tentamos refresh em 401 (Unauthorized), nunca em 403
    if (status === 401 && !isRefreshCall) {
      const token = getToken();

      // Se não tem token salvo, não há o que recuperar aqui
      if (!token) {
        hardLogout();
        return Promise.reject(error);
      }

      // Se já estamos atualizando, enfileira essa request pra rodar depois
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject, originalRequest });
        });
      }

      isRefreshing = true;

      try {
        const { data } = await api.post('/auth/refresh');
        const newToken = data?.token;
        if (!newToken) {
          throw new Error('Refresh sem token');
        }

        // Salva novo token
        setToken(newToken);

        // Libera fila pendente com o novo token
        processQueue(null, newToken);

        // Reenvia a requisição original atualizada
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (err) {
        // Falha no refresh: limpa fila, derruba sessão
        processQueue(err, null);
        hardLogout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // 🚫 403 (Forbidden) → permissão/regra de negócio, não tentamos refresh
    if (status === 403 && !isRefreshCall) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
